if (typeof ArticleFeaturedProduct !== 'function') {
  class ArticleFeaturedProduct extends HTMLElement {
    initialized = false;

    constructor() {
      super();

      this.state = {
        breadcrumbPast: false,
        stickyHeaderVisible: false,
        scrollTicking: false,
      };

      this.elements = {};

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMobileMqChange = this.onMobileMqChange.bind(this);
      this.onBreadcrumbIntersect = this.onBreadcrumbIntersect.bind(this);
    }

    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;

      this.cacheElements();
      this.readConfig();
      this.initObservers();
      this.bindEvents();
      this.updateHeaderOffset();
      this.updateMobileVisibility();
    }

    disconnectedCallback() {
      this.destroy();
    }

    cacheElements() {
      this.elements = {
        section: this.closest('.shopify-section'),
        cardWrapper: this.querySelector('.article-featured-product__card-wrapper'),
      };
    }

    readConfig() {
      this.config = {
        mobileMq: window.matchMedia('(max-width: 767px)'),
        mobileEnabled: !this.classList.contains('article-featured-product--mobile-disabled'),
        mobileTop: this.dataset.mobilePosition === 'top',
      };
    }

    bindEvents() {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      if (typeof this.config.mobileMq.addEventListener === 'function') {
        this.config.mobileMq.addEventListener('change', this.onMobileMqChange);
      } else if (typeof this.config.mobileMq.addListener === 'function') {
        this.config.mobileMq.addListener(this.onMobileMqChange);
      }
    }

    initObservers() {
      this.initBreadcrumbObserver();
      this.initStickyHeaderObserver();
    }

    initBreadcrumbObserver() {
      const breadcrumb = document.querySelector('.breadcrumb-main');

      this.breadcrumbObserver?.disconnect();
      this.breadcrumbObserver = null;

      if (!this.isBreadcrumbVisible(breadcrumb)) {
        this.state.breadcrumbPast = true;
        this.updateMobileVisibility();

        return;
      }

      const rect = breadcrumb.getBoundingClientRect();
      const intersectsViewport = rect.bottom > 0 && rect.top < window.innerHeight;

      this.state.breadcrumbPast = !intersectsViewport;

      this.breadcrumbObserver = new IntersectionObserver(this.onBreadcrumbIntersect, {
        threshold: 0,
      });

      this.breadcrumbObserver.observe(breadcrumb);
    }

    initStickyHeaderObserver() {
      const sticky = document.querySelector('.sticky-header');

      this.stickyHeaderObserver?.disconnect();
      this.stickyHeaderObserver = null;

      if (!sticky) {
        this.state.stickyHeaderVisible = false;

        return;
      }

      this.state.stickyHeaderVisible = sticky.classList.contains('show');

      this.stickyHeaderObserver = new MutationObserver(() => {
        if (!this.isConnected) {
          return;
        }

        this.state.stickyHeaderVisible = sticky.classList.contains('show');
        this.updateHeaderOffset();
        this.updateMobileVisibility();
      });

      this.stickyHeaderObserver.observe(sticky, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    onBreadcrumbIntersect(entries) {
      if (!this.isConnected) {
        return;
      }

      const entry = entries[0];

      if (!entry) {
        return;
      }

      this.state.breadcrumbPast = !entry.isIntersecting;
      this.updateMobileVisibility();
    }

    onScroll() {
      if (this.state.scrollTicking) {
        return;
      }

      this.state.scrollTicking = true;

      this.state.scrollRaf = requestAnimationFrame(() => {
        if (!this.isConnected) {
          return;
        }

        this.state.scrollTicking = false;
        this.updateHeaderOffset();
      });
    }

    onResize() {
      if (!this.isConnected) {
        return;
      }

      this.readConfig();
      this.initBreadcrumbObserver();
      this.updateHeaderOffset();
      this.updateMobileVisibility();
    }

    onMobileMqChange() {
      if (!this.isConnected) {
        return;
      }

      this.readConfig();
      this.updateMobileVisibility();
    }

    getHeaderOffsetPx() {
      const sticky = document.querySelector('.sticky-header');
      const mainEl = document.getElementById('main');

      if (sticky && sticky.classList.contains('show')) {
        return sticky.offsetHeight;
      }

      if (mainEl) {
        return Math.max(0, mainEl.getBoundingClientRect().top);
      }

      const siteHeader = document.getElementById('site-header');

      if (siteHeader) {
        return Math.max(0, siteHeader.getBoundingClientRect().bottom);
      }

      return 0;
    }

    isBreadcrumbVisible(breadcrumb) {
      if (!breadcrumb) {
        return false;
      }

      const style = window.getComputedStyle(breadcrumb);

      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }

      return breadcrumb.getBoundingClientRect().height > 0;
    }

    updateHeaderOffset() {
      if (!this.elements.section) {
        return;
      }

      this.elements.section.style.setProperty(
        '--article-featured-header-offset',
        `${this.getHeaderOffsetPx()}px`
      );
    }

    updateMobileVisibility() {
      const isMobile = this.config.mobileMq.matches;

      if (!isMobile || !this.config.mobileEnabled) {
        this.classList.add('article-featured-product--mobile-visible');

        return;
      }

      let shouldShow = this.state.breadcrumbPast;

      if (shouldShow && this.config.mobileTop && this.state.stickyHeaderVisible) {
        shouldShow = false;
      }

      this.classList.toggle('article-featured-product--mobile-visible', shouldShow);
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);

      if (this.config?.mobileMq) {
        if (typeof this.config.mobileMq.removeEventListener === 'function') {
          this.config.mobileMq.removeEventListener('change', this.onMobileMqChange);
        } else if (typeof this.config.mobileMq.removeListener === 'function') {
          this.config.mobileMq.removeListener(this.onMobileMqChange);
        }
      }

      this.breadcrumbObserver?.disconnect();
      this.stickyHeaderObserver?.disconnect();

      if (this.state.scrollRaf) {
        cancelAnimationFrame(this.state.scrollRaf);
      }

      this.breadcrumbObserver = null;
      this.stickyHeaderObserver = null;
      this.initialized = false;
    }
  }

  if (typeof customElements.get('article-featured-product') === 'undefined') {
    customElements.define('article-featured-product', ArticleFeaturedProduct);
  }
}
